import { jwtDecode } from "jwt-decode";
import axios from "axios";

// Create a dedicated axios instance for logging
const loggerAxios = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

// Add auth interceptor for authenticated requests
loggerAxios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error("Request interceptor error:", error);
    return Promise.reject(error);
  }
);

class UserJourneyLogger {
  constructor() {
    this.buffer = [];
    // REMOVED: this.flushInterval and this.flushIntervalId
    this.maxBufferSize = 1000; // Increased significantly for long sessions
    this.userId = null;
    this.userRole = null;
    this.sessionId = null;
    this.sessionStartTime = Date.now();
    this.initialized = false;
    this.heartbeatInterval = null;
    
    // REMOVED: Periodic flush interval
    // this.flushIntervalId = setInterval(() => this.flushLogs(), this.flushInterval);
    
    // Keep all logout detection methods
    this.setupLogoutDetection();
  }
  
  setupLogoutDetection() {
    // Method 1: beforeunload (traditional but unreliable)
    window.addEventListener('beforeunload', () => {
      console.log('beforeunload triggered');
      this.logSessionEnd('beforeunload');
      this.flushLogs(true);
    });
    
    // Method 2: visibilitychange (when tab becomes hidden)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        console.log('Page became hidden - potential logout');
        this.logSessionEnd('page_hidden');
        this.flushLogs(true);
      }
    });
    
    // Method 3: pagehide (more reliable than beforeunload)
    window.addEventListener('pagehide', () => {
      console.log('pagehide triggered');
      this.logSessionEnd('pagehide');
      this.flushLogs(true);
    });
    
    // Method 4: Heartbeat to detect inactive sessions (reduced frequency)
    this.startHeartbeat();
    
    // Method 5: Watch for token removal (explicit logout)
    this.watchForTokenRemoval();
  }
  
  startHeartbeat() {
    // Reduced to every 2 minutes since it's just for analytics
    this.heartbeatInterval = setInterval(() => {
      if (this.userId && document.visibilityState === 'visible') {
        this.logEvent('session', 'heartbeat', {
          sessionDuration: Math.round((Date.now() - this.sessionStartTime) / 1000)
        });
      }
    }, 120000); // 2 minutes instead of 30 seconds
  }
  
  watchForTokenRemoval() {
    // Check if token gets removed (explicit logout)
    const originalRemoveItem = localStorage.removeItem.bind(localStorage);
    localStorage.removeItem = (key) => {
      if (key === 'token' && this.userId) {
        console.log('Token removed - explicit logout detected');
        this.logSessionEnd('token_removed');
        this.flushLogs(true); // Use beacon for logout
      }
      return originalRemoveItem(key);
    };
    
    // Also watch for localStorage.clear() which your logout function uses
    const originalClear = localStorage.clear.bind(localStorage);
    localStorage.clear = () => {
      if (this.userId) {
        console.log('LocalStorage cleared - logout detected');
        this.logSessionEnd('localstorage_cleared');
        this.flushLogs(true); // Use beacon for logout
      }
      return originalClear();
    };
  }
  
  logSessionEnd(reason) {
    if (!this.userId) return;
    
    this.logEvent('session', 'end', { 
      duration: Math.round((Date.now() - this.sessionStartTime) / 1000),
      sessionId: this.sessionId,
      reason: reason,
      totalEventsInSession: this.buffer.length + 1 // +1 for this end event
    });
  }
  
  // Call this explicitly when user clicks logout
  logExplicitLogout() {
    console.log('Explicit logout called');
    this.logSessionEnd('user_logout_button');
    this.flushLogs(true); // Use beacon for reliability
  }
  
  // Call this when JWT expires (from ProtectedRoute)
  logJWTExpiry() {
    console.log('JWT expiry logout called');
    this.logSessionEnd('jwt_expired');
    this.flushLogs(true); // Use beacon for reliability
  }
  
  init() {
    if (this.initialized) return;
    
    // Generate a unique session ID
    this.sessionId = Date.now().toString(36) + Math.random().toString(36).substring(2);
    
    // Get user info from token
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        
        this.userId = decodedToken.id || 
                      decodedToken.sub || 
                      decodedToken.userId || 
                      null;
                      
        this.userRole = decodedToken.role || decodedToken.roles?.[0] || 'Teacher';
        
        if (this.userId) {
          // Check for previous unclosed sessions
          this.checkForUnfinishedSessions();
          
          // Log session start
          this.logEvent('session', 'start', { 
            browser: navigator.userAgent,
            sessionId: this.sessionId,
            screenSize: `${window.innerWidth}x${window.innerHeight}`,
            timestamp: new Date().toISOString()
          });
          
          this.initialized = true;
          console.log(`Journey tracking initialized for user: ${this.userId} (logout-only mode)`);
        } else {
          console.warn("Could not extract userId from token. User journey logging disabled.");
        }
      } catch (e) {
        console.error("Failed to decode token for journey tracking", e);
      }
    } else {
      console.warn("No token found in localStorage. User journey logging disabled.");
    }
  }
  
  checkForUnfinishedSessions() {
    // Check if we have a previous session that wasn't properly closed
    const lastSessionId = localStorage.getItem('lastSessionId');
    if (lastSessionId && lastSessionId !== this.sessionId) {
      // Log end for the previous session
      this.logEvent('session', 'end', {
        sessionId: lastSessionId,
        reason: 'session_recovered',
        duration: 0 // Unknown duration
      });
    }
    
    // Store current session ID
    localStorage.setItem('lastSessionId', this.sessionId);
  }
  
  logEvent(category, action, metadata = {}, customUrl = null) {
    if (!this.userId) return;
    
    const event = {
      userId: this.userId,
      userRole: this.userRole,
      category,
      action,
      metadata: {
        ...metadata,
        sessionId: this.sessionId
      },
      timestamp: new Date().toISOString(),
      url: customUrl || window.location.pathname
    };
    
    this.buffer.push(event);
    console.log("Event logged:", category, action, metadata.timeInSeconds ? `(${metadata.timeInSeconds}s)` : '', `(buffer: ${this.buffer.length})`);
    
    // Safety valve: flush if buffer gets extremely large (prevents memory issues)
    if (this.buffer.length >= this.maxBufferSize) {
      console.warn('Buffer reached maximum size, force flushing logs');
      this.flushLogs();
    }
  }
  
  flushLogs(useBeacon = false) {
    if (this.buffer.length === 0) return;
    
    const logs = [...this.buffer];
    this.buffer = [];
    
    console.log("Flushing logs, count:", logs.length, useBeacon ? "(using Beacon API)" : "(using axios)");
    
    // Use Beacon API if page is unloading or explicitly requested
    if (useBeacon && navigator.sendBeacon) {
      try {
        const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
        const endpoint = baseUrl + '/user-logging';
        
        const blob = new Blob([JSON.stringify({ logs })], { 
          type: 'application/json' 
        });
        
        const success = navigator.sendBeacon(endpoint, blob);
        console.log("Beacon API result:", success);
        return success;
      } catch (e) {
        console.error("Error using Beacon API:", e);
        // Fallback to axios if beacon fails
      }
    }
    
    // Otherwise use axios
    this.sendWithAxios(logs);
  }
  
  sendWithAxios(logs) {
    console.log("Sending logs with axios to: /user-logging");
    
    loggerAxios.post('/user-logging', { logs })
      .then(response => {
        console.log("Logs sent successfully:", response.status);
      })
      .catch(error => {
        console.error("Failed to send logs:", error.message);
        
        if (error.response) {
          console.error("Server response:", error.response.status, error.response.data);
        } else {
          console.error("Network error sending logs");
        }
        
        // Could implement retry logic here if needed
      });
  }
  
  cleanup() {
    // Clear heartbeat interval
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }
    
    // REMOVED: flushIntervalId cleanup (no longer exists)
    
    // Log session end and flush remaining logs
    if (this.userId) {
      this.logSessionEnd('manual_cleanup');
      this.flushLogs(true); // Use beacon for reliability
    }
    
    // Clean up localStorage
    localStorage.removeItem('lastSessionId');
  }
}

// Create singleton instance
const userJourney = new UserJourneyLogger();
export default userJourney;